import { useEffect, useRef, useState } from 'react';
import { Plus, Tag, X, AlertCircle, Power, Trash2, RotateCcw, Pencil, ChevronDown, Users } from 'lucide-react';
import { api, formatDateTime, formatMoney } from '../../lib/api';
import DateTimePicker from '../../components/admin/DateTimePicker';
import { utcToEastern, easternToUtc } from '../../lib/eastern-time';
import { useScrollShadow, STICKY_CELL, STICKY_HEAD_BG, STICKY_SHADOW } from '../../lib/sticky-column';

export default function AdminDiscountCodes() {
  const [tableRef, shadowed] = useScrollShadow();
  const [codes, setCodes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewingUsage, setViewingUsage] = useState(null);
  const [error, setError] = useState(null);

  const reload = () =>
    api('/api/admin/discount-codes')
      .then((d) => setCodes(d.codes || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    reload();
    api('/api/admin/courses')
      .then((d) => setCourses(d.courses || []))
      .catch(() => {});
  }, []);

  const deactivate = async (id) => {
    if (!confirm('Deactivate this code? Existing purchases keep their reference; the code will no longer be redeemable.')) return;
    try {
      await api(`/api/admin/discount-codes/${id}`, { method: 'DELETE' });
      reload();
    } catch (e) {
      alert(e.message);
    }
  };

  const reactivate = async (id) => {
    try {
      await api(`/api/admin/discount-codes/${id}`, { method: 'PATCH', body: { active: true } });
      reload();
    } catch (e) {
      alert(e.message);
    }
  };

  const remove = async (id, code) => {
    if (!confirm(`Permanently remove "${code}"? This cannot be undone.`)) return;
    try {
      await api(`/api/admin/discount-codes/${id}/permanent`, { method: 'DELETE' });
      reload();
    } catch (e) {
      const msg = e.body?.error === 'still_active'
        ? 'Deactivate the code first before removing it.'
        : e.message;
      alert(msg);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Tag size={22} /> Discount codes
        </h1>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-brand text-white px-4 py-2 rounded-md font-semibold text-sm hover:bg-brand-dark inline-flex items-center gap-2"
        >
          <Plus size={16} /> New code
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded text-sm flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div ref={tableRef} className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <Th>Code</Th>
              <Th>Discount</Th>
              <Th>Applies to</Th>
              <Th>Usage</Th>
              <Th>Expires</Th>
              <Th>Status</Th>
              <Th className={`${STICKY_CELL} ${STICKY_HEAD_BG} ${shadowed ? STICKY_SHADOW : ''}`} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            ) : !codes.length ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                No discount codes yet. Create one to start a marketing campaign.
              </td></tr>
            ) : codes.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 [&>td]:align-middle">
                <td className="px-4 py-3 font-mono font-bold">{c.code}</td>
                <td className="px-4 py-3">
                  {c.discount_type === 'percent'
                    ? `${c.discount_value}% off`
                    : `$${(c.discount_value / 100).toFixed(2)} off`}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <AppliesTo code={c} />
                  {c.attendance_mode && (
                    <span className="ml-1 text-xs text-amber-600">
                      · {c.attendance_mode === 'virtual' ? 'Online only' : 'In person only'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                  {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ' / ∞'}
                  {c.unique_per_visitor && (
                    <span className="ml-1 text-xs text-amber-600" title="One use per visitor">· 1/visitor</span>
                  )}
                  <button
                    onClick={() => setViewingUsage(c)}
                    className="ml-2 text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                    title="See who used this code"
                  >
                    <Users size={13} /> Used by
                  </button>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {c.expires_at ? formatDateTime(c.expires_at) : 'Never'}
                </td>
                <td className="px-4 py-3">
                  {c.active ? (
                    <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">Active</span>
                  ) : (
                    <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-600">Inactive</span>
                  )}
                </td>
                <td className={`px-4 py-3 text-right bg-white ${STICKY_CELL} ${shadowed ? STICKY_SHADOW : ''}`}><div className="flex items-center justify-end gap-3 h-full">
                  <button
                    onClick={() => setEditing(c)}
                    className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                    title="Edit"
                  >
                    <Pencil size={13} /> Edit
                  </button>
                  {c.active ? (
                    <button
                      onClick={() => deactivate(c.id)}
                      className="text-xs text-slate-500 hover:text-rose-700 inline-flex items-center gap-1"
                      title="Deactivate"
                    >
                      <Power size={14} /> Deactivate
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => reactivate(c.id)}
                        className="text-xs text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1"
                        title="Reactivate"
                      >
                        <RotateCcw size={13} /> Reactivate
                      </button>
                      <button
                        onClick={() => remove(c.id, c.code)}
                        className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                        title="Remove permanently"
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    </>
                  )}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {showCreate && (
        <CreateModal
          courses={courses}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); reload(); }}
        />
      )}

      {editing && (
        <EditModal
          code={editing}
          courses={courses}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      {viewingUsage && (
        <UsageModal code={viewingUsage} onClose={() => setViewingUsage(null)} />
      )}
    </div>
  );
}

const STATUS_STYLES = {
  pending:   'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed:    'bg-rose-100 text-rose-700',
  refunded:  'bg-slate-200 text-slate-700',
};

// ── Used-by modal ───────────────────────────────────────
// Lists the purchases on record. The Usage counter can be higher: deleting a
// course or user permanently removes its purchases without giving the use back.
function UsageModal({ code, onClose }) {
  const [purchases, setPurchases] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api(`/api/admin/discount-codes/${code.id}/usage`)
      .then((d) => setPurchases(d.purchases || []))
      .catch((e) => setError(e.message));
  }, [code.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const live = (purchases || []).filter((p) => p.status === 'completed' || p.status === 'pending').length;
  const unaccounted = purchases ? code.used_count - live : 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-4">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold">
            Used by <span className="font-mono">{code.code}</span>
            {purchases && (
              <span className="ml-2 text-sm font-normal text-slate-500">
                {purchases.length} purchase{purchases.length === 1 ? '' : 's'}
              </span>
            )}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="m-6 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded text-sm flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
          </div>
        )}

        {unaccounted > 0 && (
          <div className="mx-6 mt-4 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded text-sm flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>
              The usage counter is {code.used_count}, but only {live} purchase{live === 1 ? ' is' : 's are'} on record.
              The other {unaccounted} belonged to purchases that have since been deleted (for example, a test
              course or account removed permanently), which doesn't give the use back.
            </span>
          </div>
        )}

        {!purchases && !error ? (
          <div className="px-6 py-8 text-center text-slate-500 text-sm">Loading…</div>
        ) : purchases && !purchases.length ? (
          <div className="px-6 py-8 text-center text-slate-500 text-sm">No purchases have used this code.</div>
        ) : purchases && (
          <div className="overflow-x-auto p-2">
            <table className="w-full text-sm">
              <thead className="text-left">
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Course</Th>
                  <Th>Date</Th>
                  <Th>Paid</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-2.5 font-medium text-slate-900">
                      {p.full_name || '—'}
                      {p.order_id && (
                        <div className="text-xs font-normal text-slate-500">
                          Group of {p.order_seat_count} · {p.order_organization || p.order_buyer_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{p.email}</td>
                    <td className="px-4 py-2.5 text-slate-700">
                      {p.course_title}
                      {p.attendance_mode && (
                        <div className="text-xs text-slate-500">
                          {p.attendance_mode === 'in_person' ? 'In person' : 'Online'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">
                      {formatDateTime(p.completed_at || p.created_at)}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700 font-mono whitespace-nowrap">
                      {formatMoney(p.amount_cents, p.currency)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${STATUS_STYLES[p.status] || 'bg-slate-100 text-slate-600'}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 ${className}`}>{children}</th>;
}

// ── Create modal ─────────────────────────────────────────
function CreateModal({ courses, onClose, onCreated }) {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [uniquePerVisitor, setUniquePerVisitor] = useState(false);
  const [showInLiveModal, setShowInLiveModal] = useState(false);
  const [attendanceMode, setAttendanceMode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [courseIds, setCourseIds] = useState([]);
  const [minSubtotal, setMinSubtotal] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = {
        code: code.trim(),
        description: description.trim() || null,
        discount_type: discountType,
        discount_value: discountType === 'percent'
          ? parseInt(discountValue, 10)
          : Math.round(parseFloat(discountValue) * 100),
        max_uses: maxUses ? parseInt(maxUses, 10) : null,
        unique_per_visitor: uniquePerVisitor,
        show_in_live_modal: showInLiveModal,
        attendance_mode: attendanceMode || null,
        expires_at: expiresAt ? easternToUtc(expiresAt) : null,
        applies_to_course_ids: courseIds,
        min_subtotal_cents: minSubtotal ? Math.round(parseFloat(minSubtotal) * 100) : 0,
      };
      await api('/api/admin/discount-codes', { method: 'POST', body });
      onCreated();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-lg my-4">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold">New discount code</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <Field label="Code">
            <input
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className={inputCls + ' font-mono uppercase'}
              placeholder="LAUNCH25"
              pattern="[A-Za-z0-9_-]{2,60}"
              title="2–60 letters, digits, hyphens, or underscores"
            />
          </Field>

          <Field label="Description (internal note)">
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputCls}
              placeholder="Spring marketing campaign"
              maxLength={500}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Discount type">
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                className={inputCls}
              >
                <option value="percent">Percent off</option>
                <option value="fixed_cents">Fixed $ off</option>
              </select>
            </Field>
            <Field label={discountType === 'percent' ? 'Percent (1–100)' : 'Amount in $'}>
              <input
                required
                type="number"
                min={discountType === 'percent' ? 1 : 0.01}
                max={discountType === 'percent' ? 100 : undefined}
                step={discountType === 'percent' ? 1 : 0.01}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className={inputCls}
                placeholder={discountType === 'percent' ? '25' : '10.00'}
              />
            </Field>
          </div>

          <CourseMultiSelect courses={courses} value={courseIds} onChange={setCourseIds} />

          <AttendanceSelect value={attendanceMode} onChange={setAttendanceMode} />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Max uses (blank = unlimited)">
              <input
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                className={inputCls}
                placeholder="100"
              />
            </Field>
            <Field label="Min subtotal in $ (optional)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={minSubtotal}
                onChange={(e) => setMinSubtotal(e.target.value)}
                className={inputCls}
                placeholder="0.00"
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={uniquePerVisitor}
              onChange={(e) => setUniquePerVisitor(e.target.checked)}
              className="rounded border-slate-300 text-brand focus:ring-brand/30"
            />
            <span className="text-sm text-slate-700">
              One use per visitor
              <span className="text-slate-400 ml-1">(each person can only use this code once)</span>
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInLiveModal}
              onChange={(e) => setShowInLiveModal(e.target.checked)}
              className="rounded border-slate-300 text-brand focus:ring-brand/30"
            />
            <span className="text-sm text-slate-700">
              Show promo pill in Live signup modal
              <span className="text-slate-400 ml-1">(displays the special price, code &amp; expiry on the matching live course)</span>
            </span>
          </label>

          <Field label="Expires at (optional)">
            <DateTimePicker value={expiresAt} onChange={setExpiresAt} dropUp />
          </Field>

          {error && (
            <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded text-sm flex items-start gap-2">
              <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="bg-brand text-white px-5 py-2 rounded-md font-semibold text-sm hover:bg-brand-dark disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create code'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Edit modal ──────────────────────────────────────────
function EditModal({ code: existing, courses, onClose, onSaved }) {
  const [description, setDescription] = useState(existing.description || '');
  const [discountType, setDiscountType] = useState(existing.discount_type);
  const [discountValue, setDiscountValue] = useState(
    existing.discount_type === 'percent'
      ? String(existing.discount_value)
      : (existing.discount_value / 100).toFixed(2)
  );
  const [maxUses, setMaxUses] = useState(existing.max_uses != null ? String(existing.max_uses) : '');
  const [uniquePerVisitor, setUniquePerVisitor] = useState(existing.unique_per_visitor ?? false);
  const [showInLiveModal, setShowInLiveModal] = useState(existing.show_in_live_modal ?? false);
  const [attendanceMode, setAttendanceMode] = useState(existing.attendance_mode || '');
  const [expiresAt, setExpiresAt] = useState(
    existing.expires_at ? utcToEastern(existing.expires_at) : ''
  );
  const [courseIds, setCourseIds] = useState(existing.applies_to_course_ids || []);
  const [minSubtotal, setMinSubtotal] = useState(
    existing.min_subtotal_cents ? (existing.min_subtotal_cents / 100).toFixed(2) : ''
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = {
        description: description.trim() || null,
        discount_type: discountType,
        discount_value: discountType === 'percent'
          ? parseInt(discountValue, 10)
          : Math.round(parseFloat(discountValue) * 100),
        max_uses: maxUses ? parseInt(maxUses, 10) : null,
        unique_per_visitor: uniquePerVisitor,
        show_in_live_modal: showInLiveModal,
        attendance_mode: attendanceMode || null,
        expires_at: expiresAt ? easternToUtc(expiresAt) : null,
        applies_to_course_ids: courseIds,
        min_subtotal_cents: minSubtotal ? Math.round(parseFloat(minSubtotal) * 100) : 0,
      };
      await api(`/api/admin/discount-codes/${existing.id}`, { method: 'PATCH', body });
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-lg my-4">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold">Edit discount code</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <Field label="Code">
            <input
              disabled
              value={existing.code}
              className={inputCls + ' font-mono uppercase bg-slate-50 text-slate-500 cursor-not-allowed'}
            />
          </Field>

          <Field label="Description (internal note)">
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputCls}
              placeholder="Spring marketing campaign"
              maxLength={500}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Discount type">
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                className={inputCls}
              >
                <option value="percent">Percent off</option>
                <option value="fixed_cents">Fixed $ off</option>
              </select>
            </Field>
            <Field label={discountType === 'percent' ? 'Percent (1–100)' : 'Amount in $'}>
              <input
                required
                type="number"
                min={discountType === 'percent' ? 1 : 0.01}
                max={discountType === 'percent' ? 100 : undefined}
                step={discountType === 'percent' ? 1 : 0.01}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className={inputCls}
                placeholder={discountType === 'percent' ? '25' : '10.00'}
              />
            </Field>
          </div>

          <CourseMultiSelect courses={courses} value={courseIds} onChange={setCourseIds} />

          <AttendanceSelect value={attendanceMode} onChange={setAttendanceMode} />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Max uses (blank = unlimited)">
              <input
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                className={inputCls}
                placeholder="100"
              />
            </Field>
            <Field label="Min subtotal in $ (optional)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={minSubtotal}
                onChange={(e) => setMinSubtotal(e.target.value)}
                className={inputCls}
                placeholder="0.00"
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={uniquePerVisitor}
              onChange={(e) => setUniquePerVisitor(e.target.checked)}
              className="rounded border-slate-300 text-brand focus:ring-brand/30"
            />
            <span className="text-sm text-slate-700">
              One use per visitor
              <span className="text-slate-400 ml-1">(each person can only use this code once)</span>
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInLiveModal}
              onChange={(e) => setShowInLiveModal(e.target.checked)}
              className="rounded border-slate-300 text-brand focus:ring-brand/30"
            />
            <span className="text-sm text-slate-700">
              Show promo pill in Live signup modal
              <span className="text-slate-400 ml-1">(displays the special price, code &amp; expiry on the matching live course)</span>
            </span>
          </label>

          <Field label="Expires at (optional)">
            <DateTimePicker value={expiresAt} onChange={setExpiresAt} dropUp />
          </Field>

          {error && (
            <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded text-sm flex items-start gap-2">
              <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="bg-brand text-white px-5 py-2 rounded-md font-semibold text-sm hover:bg-brand-dark disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Limit a code to online or in-person seats. Only hybrid events have both, so
// "Online only" changes nothing elsewhere and "In person only" never applies.
function AttendanceSelect({ value, onChange }) {
  return (
    <div>
      <Field label="Attendance">
        <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
          <option value="">Online or in person</option>
          <option value="virtual">Online only</option>
          <option value="in_person">In person only</option>
        </select>
      </Field>
      <p className="text-xs text-slate-500 mt-1">
        For hybrid events. In a group registration, only the matching attendees get the discount.
      </p>
    </div>
  );
}

function AppliesTo({ code }) {
  const ids = code.applies_to_course_ids || [];
  if (!ids.length) return 'All courses';
  const titles = code.course_titles || [];
  if (!titles.length) return <span className="text-slate-400">Deleted course{ids.length > 1 ? 's' : ''}</span>;
  if (titles.length <= 2) return titles.join(', ');
  return (
    <span title={titles.join('\n')}>
      {titles.slice(0, 2).join(', ')} <span className="text-slate-400">+{titles.length - 2} more</span>
    </span>
  );
}

// "Applies to" picker: none checked = all courses. Not wrapped in Field — a
// <label> around the checkbox rows would hijack their clicks.
function CourseMultiSelect({ courses, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = new Set(value);
  const toggle = (id) => onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  const q = filter.trim().toLowerCase();
  const shown = q ? courses.filter((c) => c.title.toLowerCase().includes(q)) : courses;

  const summary = !value.length
    ? 'All courses'
    : value.length === 1
      ? (courses.find((c) => c.id === value[0])?.title || '1 course')
      : `${value.length} courses`;

  return (
    <div ref={ref} className="relative">
      <span className="block text-xs font-semibold text-slate-600 mb-1">Applies to</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={inputCls + ' flex items-center justify-between text-left bg-white'}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown size={16} className="shrink-0 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg">
          {courses.length > 8 && (
            <div className="p-2 border-b border-slate-100">
              <input
                autoFocus
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter courses…"
                className={inputCls + ' py-1.5'}
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto py-1">
            <label className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 border-b border-slate-100">
              <input
                type="checkbox"
                checked={!value.length}
                onChange={() => onChange([])}
                className="rounded border-slate-300 text-brand focus:ring-brand/30"
              />
              <span className="text-sm font-semibold text-slate-700">All courses</span>
            </label>
            {shown.map((c) => (
              <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  className="rounded border-slate-300 text-brand focus:ring-brand/30"
                />
                <span className="text-sm text-slate-700">
                  {c.title}
                  {c.status && c.status !== 'published' && (
                    <span className="ml-1 text-xs text-slate-400">({c.status})</span>
                  )}
                </span>
              </label>
            ))}
            {!shown.length && <div className="px-3 py-2 text-sm text-slate-400">No matching courses</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand';
